interface CommandResult {
  success: boolean;
  output?: string;
  error?: string;
}

interface GitStatus {
  branch: string;
  clean: boolean;
  uncommittedFiles: string[];
  ahead: number;
  behind: number;
}

interface BuildInfo {
  exists: boolean;
  files: string[];
  size: string;
  timestamp?: string;
}

class DeploymentService {
  private apiUrl: string;

  constructor() {
    this.apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8787';
  }

  async checkGitStatus(): Promise<GitStatus> {
    try {
      // In a real implementation, this would call a backend API
      // For now, we'll simulate based on local storage or return mock data
      const mockStatus: GitStatus = {
        branch: 'main',
        clean: true,
        uncommittedFiles: [],
        ahead: 0,
        behind: 0,
      };

      // Check if we have any indication of uncommitted changes
      // Only access localStorage if in browser
      if (typeof window !== 'undefined' && localStorage) {
        const lastDeployTime = localStorage.getItem('last_deploy_time');
        const lastEditTime = localStorage.getItem('last_edit_time');

        if (lastEditTime && lastDeployTime && new Date(lastEditTime) > new Date(lastDeployTime)) {
          mockStatus.clean = false;
          mockStatus.uncommittedFiles = ['Some files have been modified'];
        }
      }

      return mockStatus;
    } catch (error) {
      throw new Error('Failed to check git status');
    }
  }

  async checkNodeVersion(): Promise<{ version: string; isCompatible: boolean }> {
    try {
      // This would typically be checked on the server
      // We'll use browser info as a proxy for now
      const userAgent = navigator.userAgent;
      const chromeVersion = userAgent.match(/Chrome\/(\d+)/)?.[1];
      const isModern = chromeVersion ? parseInt(chromeVersion) >= 90 : true;

      return {
        version: 'v18.17.0', // Mock version
        isCompatible: isModern,
      };
    } catch (error) {
      throw new Error('Failed to check Node version');
    }
  }

  async checkDependencies(): Promise<{ installed: boolean; outdated: number }> {
    try {
      // Check if the app is running properly as a proxy for dependencies
      const appRunning = typeof window !== 'undefined' && document.querySelector('#root');

      return {
        installed: !!appRunning,
        outdated: 0, // Would need backend to check this
      };
    } catch (error) {
      throw new Error('Failed to check dependencies');
    }
  }

  async checkBuildDirectory(): Promise<BuildInfo> {
    try {
      // Check if we can access static assets as a proxy for build success
      const response = await fetch('/index.html', { method: 'HEAD' });

      return {
        exists: response.ok,
        files: ['index.html', 'assets/index.js', 'assets/index.css'],
        size: '1.58 MB',
        timestamp: undefined, // Avoid hydration issues with dynamic timestamps
      };
    } catch (error) {
      throw new Error('Failed to check build directory');
    }
  }

  async checkCloudflareAuth(): Promise<{ authenticated: boolean; method: string }> {
    try {
      // Check if we have any Cloudflare indicators
      const hasCloudflareHeaders = 'CF-Ray' in (await fetch('/').then(r => Object.fromEntries(r.headers.entries())));

      return {
        authenticated: true, // Assume authenticated if deployed
        method: hasCloudflareHeaders ? 'wrangler login (active)' : 'wrangler login (required)',
      };
    } catch (error) {
      return {
        authenticated: false,
        method: 'wrangler login (required)',
      };
    }
  }

  async checkProductionAPI(): Promise<{ reachable: boolean; responseTime: number; status: number }> {
    try {
      const startTime = performance.now();
      const response = await fetch('https://biig-api.b-9f2.workers.dev/api/health', {
        method: 'GET',
        mode: 'cors',
      }).catch(() => null);

      const responseTime = performance.now() - startTime;

      if (!response) {
        // Try alternative endpoint
        const altResponse = await fetch('https://biig-api.b-9f2.workers.dev/', {
          method: 'HEAD',
          mode: 'no-cors', // Allow opaque response
        });

        return {
          reachable: true, // If we get here without error, assume it's reachable
          responseTime,
          status: 0, // Opaque response doesn't give status
        };
      }

      return {
        reachable: response.ok,
        responseTime,
        status: response.status,
      };
    } catch (error) {
      throw new Error('Production API unreachable');
    }
  }

  async getEnvironmentVariables(): Promise<{ configured: string[]; missing: string[] }> {
    const required = [
      'VITE_API_URL',
      'VITE_PROD_API_URL',
      'VITE_FILES_URL',
      'VITE_PROD_FILES_URL',
    ];

    const configured = required.filter(key => import.meta.env[key]);
    const missing = required.filter(key => !import.meta.env[key]);

    return { configured, missing };
  }

  async getDeploymentHistory(): Promise<Array<{ id: string; timestamp: string; branch: string; status: string }>> {
    try {
      // Only access localStorage if in browser
      if (typeof window !== 'undefined' && localStorage) {
        const history = localStorage.getItem('deployment_history');
        if (history) {
          return JSON.parse(history);
        }
      }

      // Use a fixed timestamp to avoid hydration issues
      return [
        {
          id: '681e4bb7',
          timestamp: '2024-01-19T17:53:00.000Z',
          branch: 'main',
          status: 'success',
        },
      ];
    } catch {
      return [];
    }
  }

  async simulateDeployment(): Promise<{ success: boolean; url: string; deploymentId: string }> {
    // Simulate deployment process
    await new Promise(resolve => setTimeout(resolve, 2000));

    const deploymentId = Math.random().toString(36).substring(2, 10);
    const url = `https://${deploymentId}.biig-web.pages.dev`;

    // Save to history - only save if in browser and after mount
    if (typeof window !== 'undefined' && localStorage) {
      const history = await this.getDeploymentHistory();
      const timestamp = new Date().toISOString();
      history.unshift({
        id: deploymentId,
        timestamp: timestamp,
        branch: 'main',
        status: 'success',
      });
      localStorage.setItem('deployment_history', JSON.stringify(history.slice(0, 10)));
      localStorage.setItem('last_deploy_time', timestamp);
    }

    return {
      success: true,
      url,
      deploymentId,
    };
  }

  async verifyDeployment(url: string): Promise<{ alive: boolean; routes: Record<string, boolean> }> {
    const routes = ['/', '/links', '/science', '/whatsapp-templates'];
    const results: Record<string, boolean> = {};

    for (const route of routes) {
      try {
        const response = await fetch(`${url}${route}`, {
          method: 'HEAD',
          mode: 'no-cors', // Allow checking external URLs
        });
        // With no-cors, we can't read the response but no error means it's reachable
        results[route] = true;
      } catch {
        results[route] = false;
      }
    }

    return {
      alive: results['/'] === true,
      routes: results,
    };
  }
}

export const deploymentService = new DeploymentService();